// `chrome-extension.automation-criteria` — the automation configuration screen (plan §7 phase 5).
//
// ⚠️ IT HANGS OFF THE ROOT STATE, NOT OFF THE TAB CHROME, even though it lives in
// `extension.state.tabs.config.ng1.ts` with the extension screens. Its state name is
// `chrome-extension.automation-criteria` — one segment, no `extension-tabs` in it — so it renders
// no tab strip and no header, which is what its reference capture shows. `hideTabIndicators: true`
// in its `data` is then belt and braces rather than the thing doing the work.
//
// ⚠️ AND BOTH HALVES SEND THE SAME BODY TO TWO DIFFERENT ENDPOINTS. The prediction counter and
// the Save button post the identical edited configuration; only the path differs. That is why
// the prediction is re-requested on every Copilot change and on none of the candidate ones —
// upstream calls `updatePrediction()` from `onCopilotConfigChange` alone, and the counter does
// not move when you tick a candidate filter. Reproduced rather than tidied: it is the screen.
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import {
  fetchViewModel,
  AtsFiltersFieldType,
  getAtsFiltersConfiguration,
  getAtsFiltersSingleselectValues,
  getAutoMatchingConfig,
  getAutoMatchingPrediction,
  type AtsFiltersFieldConfiguration,
  type AtsFiltersSingleselectValues,
  type AutomationCandidateCriteria as CandidateCriteriaDto,
  type AutomationCopilotConfig,
} from '@woo-ext/api'
import {
  AutomationCandidateCriteria,
  AutomationCopilot,
  AutomationCriteriaPage,
  type AtsFilterRow,
  getAtsLabel,
} from '@woo-ext/ui'
import { useRouteChrome } from './useRouteChrome.ts'

/** `ATSType.BULLHORN` — the only ATS with users assigned to a job. */
const BULLHORN = 'Bullhorn'

/**
 * `initAtsFiltersCriteria` — the table starts with ONE EMPTY ROW when nothing is saved.
 *
 * ⚠️ NOT AN EMPTY TABLE. Both filter tables call this, and a tenant with no saved filters gets
 * a row with its Field select on "Please select..." rather than an ADD link over nothing. The
 * reference capture holds exactly that on the candidate half and two saved rows on the job one.
 */
function rowsOrOneEmpty(saved: readonly unknown[] | undefined | null): AtsFilterRow[] {
  return saved === undefined || saved === null || saved.length === 0 ? [{}] : (saved as AtsFilterRow[])
}

/** `initCandidateCriteria`'s fallback: the only flag on this screen that defaults to true. */
const DEFAULT_CANDIDATE_CRITERIA: CandidateCriteriaDto = {
  candidateOwnerJobOwner: false,
  excludeCandidateWithCurrentJobSubmission: true,
}

/** `initCopilotCriteria`'s fallback. */
const DEFAULT_COPILOT: AutomationCopilotConfig = {
  active: false,
  sendToJobOwner: true,
  sendToAssignedUsers: false,
}

/**
 * `mapCopilotConfigToTempConfig` — the DTO's array becomes the input's comma-separated string.
 *
 * ⚠️ AND BACK AGAIN ON SAVE. Binding the input straight to `additionalRecipients` would send the
 * string where the server wants a list, and store one recipient called
 * "a@b.com,c@d.com" — which the screen would then render back, looking correct.
 */
export function recipientsToField(recipients: readonly string[] | null | undefined): string {
  // ⚠️ `null`, NOT `undefined`, IS WHAT THE SERVER SENDS. `getConfigData` answers
  // `"additionalRecipients": null` and `"backupRecipient": null` for a tenant that has set
  // neither, so a `=== undefined` check reaches `null.join` and throws — inside a `.then`,
  // which takes the rest of the callback with it: the two `setRows` calls below it never ran
  // and both filter tables rendered empty while the checkboxes beside them showed saved values.
  // Upstream's own guard is `!!initialAutomationCopilotConfig.additionalRecipients`.
  return recipients === undefined || recipients === null ? '' : recipients.join(',')
}

export function fieldToRecipients(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
}

export function AutomationCriteria(): ReactElement {
  // No layout above this route, so it applies its own chrome — see `standalone.tsx`.
  useRouteChrome()

  const [criteria, setCriteria] = useState<CandidateCriteriaDto>(DEFAULT_CANDIDATE_CRITERIA)
  const [copilot, setCopilot] = useState<AutomationCopilotConfig>(DEFAULT_COPILOT)
  const [recipientsField, setRecipientsField] = useState('')
  const [prediction, setPrediction] = useState<number | undefined>(undefined)
  const [atsName, setAtsName] = useState<string | undefined>(undefined)
  /** The two collapsible titles say the LABEL; Bullhorn's checkbox keys on `atsName`. */
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  const [candidateFields, setCandidateFields] = useState<readonly AtsFiltersFieldConfiguration[]>([])
  const [jobFields, setJobFields] = useState<readonly AtsFiltersFieldConfiguration[]>([])
  // One map PER TABLE, as upstream has one per component: the candidate table's options come from
  // the candidate configuration's sections, the copilot job table's from the job configuration's.
  const [candidateSingleselectValues, setCandidateSingleselectValues] = useState<AtsFiltersSingleselectValues>({})
  const [jobSingleselectValues, setJobSingleselectValues] = useState<AtsFiltersSingleselectValues>({})
  const [candidateRows, setCandidateRows] = useState<AtsFilterRow[]>([{}])
  const [jobRows, setJobRows] = useState<AtsFilterRow[]>([{}])

  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        setAtsName(model.atsName ?? undefined)
        setAtsLabel(getAtsLabel(model))
      },
      () => undefined,
    )
    // ⚠️ TWO CONFIGURATIONS, TWO ENTITIES. The candidate table asks for the default
    // (CANDIDATE) and the copilot's job table asks for JOB — a tenant can have filterable job
    // fields and no candidate ones, and one call for both would render the wrong table's fields.
    /**
     * `initAtsFiltersConfig()`, once per table, in upstream's order: the configuration, then the
     * options for its SINGLE_SELECT fields only, and no call at all when there are none.
     *
     * ⚠️ ONLY THE SINGLE_SELECT SECTIONS. This sent the section of EVERY field (CREATION_DATE,
     * OWNER, STATUS…), which the endpoint answers with a 500, and the network-error alert then
     * covered the screen (measured on the dev tenant, 28 Oct, 89.5% of the reference).
     *
     * ⚠️ THE VALUES CALL SAYS **CANDIDATE** FOR BOTH TABLES. Upstream's copilot component asks
     * `getConfigurationsByWhitelabel(JOB)` and then `getAtsFiltersSingleselectValuesBySections(
     * sections, CANDIDATE)`: the entity changes between the two lines. Copying JOB through to the
     * second call is the obvious tidy-up and answers a different set of options.
     */
    const loadTable = (
      entity: 'JOB' | undefined,
      setFields: (fields: readonly AtsFiltersFieldConfiguration[]) => void,
      setValues: (values: AtsFiltersSingleselectValues) => void,
    ): void => {
      getAtsFiltersConfiguration(entity).then(
        (configuration) => {
          if (!live) return
          const fields = configuration.fields ?? []
          setFields(fields)
          const sections = [
            ...new Set(
              fields
                .filter((field) => field.type === AtsFiltersFieldType.SingleSelect)
                .map((field) => field.section)
                .filter((section): section is string => section !== undefined),
            ),
          ]
          if (sections.length === 0) return
          getAtsFiltersSingleselectValues(sections, 'CANDIDATE').then(
            (values) => {
              if (live) setValues(values)
            },
            () => undefined,
          )
        },
        () => undefined,
      )
    }
    // ⚠️ TWO CONFIGURATIONS, TWO ENTITIES. The candidate table asks for the default (CANDIDATE)
    // and the copilot's job table asks for JOB: a tenant can have filterable job fields and no
    // candidate ones, and one call for both would render the wrong table's fields.
    loadTable(undefined, setCandidateFields, setCandidateSingleselectValues)
    loadTable('JOB', setJobFields, setJobSingleselectValues)
    getAutoMatchingConfig().then(
      (config) => {
        if (!live) return
        const saved = config.automationCandidateCriteria ?? DEFAULT_CANDIDATE_CRITERIA
        const savedCopilot = config.automationCopilotConfig ?? DEFAULT_COPILOT
        setCriteria(saved)
        setCopilot(savedCopilot)
        setRecipientsField(recipientsToField(savedCopilot.additionalRecipients))
        setCandidateRows(rowsOrOneEmpty(saved.atsFilters))
        setJobRows(rowsOrOneEmpty(savedCopilot.jobCriteria?.atsFilters))
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  const requestPrediction = useCallback(
    (nextCopilot: AutomationCopilotConfig, nextRows: readonly AtsFilterRow[], recipients: string) => {
      // `updatePrediction` clears it first, which is what puts the spinner back while the new
      // number is in flight.
      setPrediction(undefined)
      getAutoMatchingPrediction({
        automationCandidateCriteria: { ...criteria, atsFilters: candidateRows },
        automationCopilotData: {
          ...nextCopilot,
          additionalRecipients: fieldToRecipients(recipients),
          jobCriteria: { atsFilters: nextRows },
        },
      }).then(
        (answer) => {
          setPrediction(answer.results)
        },
        () => undefined,
      )
    },
    [criteria, candidateRows],
  )

  // The first prediction, once the saved configuration has landed. Upstream asks for it at the
  // end of `$onInit`, after both halves are initialised.
  useEffect(() => {
    requestPrediction(copilot, jobRows, recipientsField)
    // Only on mount and when the saved config replaces the defaults: the copilot handlers below
    // request it themselves, and depending on `copilot` here would ask twice for every toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria, candidateRows.length])

  const updateCopilot = (next: AutomationCopilotConfig, rows: readonly AtsFilterRow[] = jobRows, recipients = recipientsField): void => {
    setCopilot(next)
    requestPrediction(next, rows, recipients)
  }

  return (
    <AutomationCriteriaPage {...(prediction === undefined ? {} : { prediction })}>
      <AutomationCandidateCriteria
        ownerIsJobOwner={criteria.candidateOwnerJobOwner}
        excludeWithJobSubmission={criteria.excludeCandidateWithCurrentJobSubmission}
        {...(atsLabel === undefined ? {} : { atsLabel })}
        filterRows={candidateRows}
        filterFields={candidateFields}
        singleselectValues={candidateSingleselectValues}
        onOwnerIsJobOwnerChange={(value) => {
          setCriteria((previous) => ({ ...previous, candidateOwnerJobOwner: value }))
        }}
        onExcludeWithJobSubmissionChange={(value) => {
          setCriteria((previous) => ({ ...previous, excludeCandidateWithCurrentJobSubmission: value }))
        }}
        onFilterChange={(index, row) => {
          setCandidateRows((previous) => previous.map((existing, at) => (at === index ? row : existing)))
        }}
        onFilterRemove={(index) => {
          setCandidateRows((previous) => previous.filter((_, at) => at !== index))
        }}
        onFilterAdd={() => {
          setCandidateRows((previous) => [...previous, {}])
        }}
      />
      <AutomationCopilot
        active={copilot.active}
        sendToJobOwner={copilot.sendToJobOwner}
        sendToAssignedUsers={copilot.sendToAssignedUsers}
        additionalRecipients={recipientsField}
        backupRecipient={copilot.backupRecipient ?? ''}
        {...(atsLabel === undefined ? {} : { atsLabel })}
        assignedUsersEnabled={atsName === BULLHORN}
        jobFilterRows={jobRows}
        jobFilterFields={jobFields}
        singleselectValues={jobSingleselectValues}
        onActiveChange={(value) => {
          updateCopilot({ ...copilot, active: value })
        }}
        onSendToJobOwnerChange={(value) => {
          updateCopilot({ ...copilot, sendToJobOwner: value })
        }}
        onSendToAssignedUsersChange={(value) => {
          updateCopilot({ ...copilot, sendToAssignedUsers: value })
        }}
        onAdditionalRecipientsChange={(value) => {
          setRecipientsField(value)
          updateCopilot(copilot, jobRows, value)
        }}
        onBackupRecipientChange={(value) => {
          updateCopilot({ ...copilot, backupRecipient: value })
        }}
        onJobFilterChange={(index, row) => {
          const next = jobRows.map((existing, at) => (at === index ? row : existing))
          setJobRows(next)
          updateCopilot(copilot, next)
        }}
        onJobFilterRemove={(index) => {
          const next = jobRows.filter((_, at) => at !== index)
          setJobRows(next)
          updateCopilot(copilot, next)
        }}
        onJobFilterAdd={() => {
          const next = [...jobRows, {}]
          setJobRows(next)
          updateCopilot(copilot, next)
        }}
      />
    </AutomationCriteriaPage>
  )
}
